"use client"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { Phone, MapPin } from "lucide-react"
import { getTokenFromStorage, getLocalIdFromToken } from "@/lib/auth"

export default function MinimalMenuHero() {
    const [data, setData] = useState<any>(null)
    const [localId, setLocalId] = useState<string | null>(null)

    useEffect(() => {
        const token = getTokenFromStorage()
        if (token) {
            setLocalId(getLocalIdFromToken(token))
        }
    }, [])

    useEffect(() => {
        if (!localId) return

        const cached = localStorage.getItem(`localData_${localId}`)
        if (cached) setData(JSON.parse(cached))

        fetch(`https://api.syncmenu.ao/generic/estabelecimento/${localId}`)
            .then((res) => res.json())
            .then((json) => {
                const content = json.localDeAtendimento
                setData(content)
                localStorage.setItem(`localData_${localId}`, JSON.stringify(content))
            })
            .catch(console.error)
    }, [localId])

    if (!data) {
        return (
            <div className="h-[60vh] w-full bg-background flex items-center justify-center border-b-8 border-foreground">
                <div className="w-12 h-12 border-4 border-foreground border-t-transparent animate-spin" />
            </div>
        )
    }

    return (
        <div className="relative bg-background border-b-8 border-foreground py-16 px-6">
            <div className="max-w-5xl mx-auto">
                {/* Stark, Grid-Based Layout */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-4 border-foreground">
                    {/* Left: Text */}
                    <div className="p-8 md:p-12 flex flex-col justify-center border-r-0 md:border-r-4 border-b-4 md:border-b-0 border-foreground">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.4 }}
                        >
                            <div className="font-mono text-xs uppercase tracking-widest mb-4 text-foreground/70">
                                MENU DIGITAL
                            </div>

                            <h1 className="text-5xl md:text-7xl font-bold text-foreground mb-6 leading-none">
                                {data.estabelecimentoId.nome}
                            </h1>

                            <div className="h-2 w-24 bg-foreground mb-6" />

                            <p className="font-mono text-base text-foreground/80 uppercase tracking-wider mb-8">
                                {data.secaoId.nome}
                            </p>

                            {/* Info Grid - Stark minimal */}
                            <div className="grid grid-cols-1 gap-4 font-mono text-sm">
                                <div className="flex items-center gap-3 border-l-4 border-foreground pl-4">
                                    <MapPin className="w-4 h-4" />
                                    <span>{data.nome}</span>
                                </div>

                                <div className="flex items-center gap-3 border-l-4 border-foreground pl-4">
                                    <Phone className="w-4 h-4" />
                                    <span>{data.estabelecimentoId.telefone || "---"}</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>

                    {/* Right: Geometric Pattern */}
                    <div className="aspect-square md:aspect-auto bg-foreground relative overflow-hidden">
                        <div className="absolute inset-0 opacity-10">
                            {/* Grid Pattern */}
                            <div className="grid grid-cols-8 grid-rows-8 h-full w-full">
                                {Array.from({ length: 64 }).map((_, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: i % 3 === 0 ? 1 : 0 }}
                                        transition={{ delay: i * 0.01 }}
                                        className="border border-background"
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Centered Logo/Symbol */}
                        <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-32 h-32 border-8 border-background" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
